import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  Spinner,
  useToast,
} from "../../design-system";
import {
  downloadReportJob,
  listReportJobs,
} from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { formatInstant } from "../../helpers/datetime";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const STATUS_META = {
  queued:     { label: "En cola",       tone: "neutral", icon: "Hourglass" },
  processing: { label: "Generando…",    tone: "info",    icon: "Loader2" },
  ready:      { label: "Listo",         tone: "success", icon: "CheckCircle2" },
  failed:     { label: "Falló",         tone: "danger",  icon: "XCircle" },
};

const TYPE_LABEL = {
  hours: "Horas trabajadas",
  absences: "Ausencias",
};

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Instante en la zona del workspace.
function fmtDateTime(iso, tz) {
  if (!iso) return "";
  return formatInstant(iso, tz);
}

/**
 * Sección "Mis reportes" — lista los jobs de cola del usuario actual.
 * Auto-refresca cada 5s mientras haya jobs en queued/processing.
 */
export function MyReportsCard() {
  const toast = useToast();
  const { timezone } = useCurrentMembership();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await listReportJobs({ limit: 20 });
      setJobs(unwrap(r) || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudieron cargar los reportes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling adaptativo: solo refresca si hay jobs en proceso.
  const hasInFlight = useMemo(
    () => jobs.some((j) => j.status === "queued" || j.status === "processing"),
    [jobs],
  );

  useEffect(() => {
    if (!hasInFlight) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return undefined;
    }
    timerRef.current = setInterval(load, 5000);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [hasInFlight, load]);

  const handleDownload = async (job) => {
    try {
      const ext = job.format;
      const range = `${job.params?.start_date || ""}_${job.params?.end_date || ""}`;
      await downloadReportJob(job.id, `${job.type}_${range}.${ext}`);
    } catch (e) {
      toast.error("No se pudo descargar el archivo", e?.message || "");
    }
  };

  return (
    <Card>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
            Mis reportes generados
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Los reportes que solicitas en segundo plano. Quedan disponibles para descargar.
          </div>
        </div>
        <Button variant="ghost" size="sm" icon="RefreshCcw" onClick={load} disabled={loading}>
          Actualizar
        </Button>
      </div>

      {error && <Alert tone="danger" style={{ marginBottom: 8 }}>{error}</Alert>}

      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
      ) : jobs.length === 0 ? (
        <EmptyState
          icon="FileBarChart"
          title="Aún no has generado reportes"
          description="Pulsa 'Generar PDF' o 'Generar CSV' arriba para crear tu primero."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {jobs.map((j) => {
            const meta = STATUS_META[j.status] || STATUS_META.queued;
            const range = `${j.params?.start_date || "?"} → ${j.params?.end_date || "?"}`;
            return (
              <div key={j.id} style={{
                display: "grid",
                gridTemplateColumns: "1fr 120px 120px 160px auto",
                gap: 10, alignItems: "center",
                padding: "10px 12px",
                border: "1px solid var(--kr-border-subtle)",
                borderRadius: 6,
                background: j.status === "ready" ? "#fff" : "var(--kr-neutral-50)",
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                    {TYPE_LABEL[j.type] || j.type} · {String(j.format || "").toUpperCase()}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2, fontFamily: "var(--kr-font-mono)" }}>
                    {range}
                  </div>
                  {j.status === "failed" && j.failed_reason && (
                    <div style={{ fontSize: 11, color: "var(--kr-danger-700)", marginTop: 4 }}>
                      {j.failed_reason.slice(0, 160)}
                    </div>
                  )}
                </div>
                <Badge tone={meta.tone} dot>{meta.label}</Badge>
                <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>
                  {fmtBytes(j.file_size_bytes)}
                </div>
                <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                  {fmtDateTime(j.requested_at, timezone)}
                </div>
                <div>
                  {j.status === "ready" ? (
                    <Button variant="secondary" size="sm" icon="Download" onClick={() => handleDownload(j)}>
                      Descargar
                    </Button>
                  ) : j.status === "processing" || j.status === "queued" ? (
                    <Icon name="Loader2" size={14} style={{ color: "var(--kr-fg-muted)" }} />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default MyReportsCard;
